import { Descriptions, Drawer, Flex, Table, Tag, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { RoutesPaths } from "../../../../constants/routes.ts"
import {
  formatDate,
  formatMoney,
  formatPoints,
  formatSignedDays
} from "../format"
import type { ProjectMilestone, ProjectRow } from "./types"

interface Props {
  row: ProjectRow | null
  amountsVisible: boolean
  onClose: () => void
}

const percent = (value: number | null) =>
  value === null ? "—" : `${value.toFixed(1)}%`

// Every value a score was built from, for one project (spec §4 "Project evidence drawer").
export function EvidenceDrawer({ row, amountsVisible, onClose }: Props) {
  const { t } = useTranslation()
  const money = (value: number | null) =>
    amountsVisible ? formatMoney(value) : "—"
  return (
    <Drawer
      open={!!row}
      onClose={onClose}
      width={560}
      title={row?.name}
      destroyOnHidden
    >
      {row && (
        <Flex vertical gap="large">
          <Descriptions
            size="small"
            column={1}
            title={t("teamPerformance.evidence.eligibility")}
            items={[
              {
                label: t("teamPerformance.evidence.status"),
                children: t("teamPerformance.evidence.completed")
              },
              {
                label: t("teamPerformance.evidence.closedDate"),
                children: formatDate(row.closed_date)
              },
              {
                label: t("teamPerformance.evidence.type"),
                children:
                  row.project_type ?? t("teamPerformance.filters.unknownType")
              },
              {
                label: t("teamPerformance.evidence.quality"),
                children: row.quality_flags.length
                  ? row.quality_flags.map((f) => (
                      <Tag key={f}>
                        {t(`teamPerformance.qualityFlags.${f}`)}
                      </Tag>
                    ))
                  : t("teamPerformance.evidence.noIssues")
              }
            ]}
          />
          <Descriptions
            size="small"
            column={1}
            title={t("teamPerformance.evidence.schedule")}
            items={[
              {
                label: t("teamPerformance.evidence.plannedCompletion"),
                children: formatDate(row.planned_end)
              },
              {
                label: t("teamPerformance.evidence.actualCompletion"),
                children: `${formatDate(row.actual_end)} · ${t(`teamPerformance.evidence.source.${row.actual_end_source}`)}`
              },
              {
                label: t("teamPerformance.evidence.variance"),
                children: formatSignedDays(row.variance_days)
              },
              {
                label: t("teamPerformance.evidence.result"),
                children:
                  row.variance_days === null
                    ? t("teamPerformance.evidence.notMeasured")
                    : row.variance_days > 0
                      ? t("teamPerformance.evidence.late")
                      : t("teamPerformance.evidence.onTime")
              }
            ]}
          />
          <Descriptions
            size="small"
            column={1}
            title={t("teamPerformance.evidence.financial")}
            items={[
              {
                label: t("teamPerformance.evidence.expectedIncome"),
                children: money(row.expected_income)
              },
              {
                label: t("teamPerformance.evidence.billed"),
                children: money(row.billed)
              },
              {
                label: t("teamPerformance.kpi.revenueGap"),
                children: money(row.revenue_gap)
              },
              {
                label: t("teamPerformance.evidence.budget"),
                children: money(row.budget)
              },
              {
                label: t("teamPerformance.evidence.cost"),
                children: money(row.cost)
              },
              {
                label: t("teamPerformance.kpi.costOverrun"),
                children: money(row.cost_overrun)
              },
              {
                label: t("teamPerformance.evidence.margin"),
                children: amountsVisible
                  ? `${percent(row.planned_margin_percent)} → ${percent(row.actual_margin_percent)} (${formatPoints(row.margin_variance_points)})`
                  : "—"
              }
            ]}
          />
          <div>
            <Typography.Title level={5}>
              {t("teamPerformance.evidence.milestones")}
            </Typography.Title>
            <Table<ProjectMilestone>
              size="small"
              rowKey={(m) => `${m.key}-${m.planned}`}
              pagination={false}
              dataSource={row.milestones}
              locale={{ emptyText: t("teamPerformance.evidence.noMilestones") }}
              columns={[
                {
                  key: "label",
                  title: t("teamPerformance.milestones.milestone"),
                  dataIndex: "label"
                },
                {
                  key: "planned",
                  title: t("teamPerformance.evidence.planned"),
                  render: (_, m) => formatDate(m.planned)
                },
                {
                  key: "actual",
                  title: t("teamPerformance.evidence.actual"),
                  render: (_, m) => formatDate(m.actual)
                },
                {
                  key: "variance",
                  title: t("teamPerformance.evidence.variance"),
                  align: "right",
                  render: (_, m) => formatSignedDays(m.variance_days)
                }
              ]}
            />
          </div>
          <Typography.Text type="secondary">
            {t("teamPerformance.evidence.audit")}{" "}
            <Link to={RoutesPaths.project.view(row.id).overview}>
              {t("teamPerformance.evidence.openProject")}
            </Link>
          </Typography.Text>
        </Flex>
      )}
    </Drawer>
  )
}
